import React from "react";
import {
  FileUploadStatusItemProps,
  UploadStatus,
} from "./FileUploadStatus.types";
import { ProgressBar, ProgressFill } from "./FileUploadStatus.styles";
import { FlexColumn } from "../../../components/Layout/FlexColumn/FlexColumn";
import { Box } from "../../../primitives/Box/Box";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import { Icon } from "../../../components/Icon/Icon";
import { colorPalette } from "../../../theme/colors";
import {
  Body6MediumTypography,
  Caps2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import { getFileUploadStatusColor } from "../../../utils";
import { Tags } from "../../../components/Tags/Tags";
import { ActionButton } from "../../../components/ActionButton/ActionButton";

const FileUploadStatusItem: React.FC<FileUploadStatusItemProps> = ({
  fileName,
  status,
  progress = 0,
  fileSize,
  submissionDate,
  onCancel,
  onRetry,
  onDelete,
  dataCy,
}) => {
  const getStatusText = (status: UploadStatus) => {
    switch (status) {
      case "uploading":
        return `${Math.round(progress)}%`;
      case "uploaded":
        return `UPLOADED ${fileSize}`;
      case "failed":
        return "ERROR IN UPLOADING";
      case "DRAFT_PENDING":
      case "DRAFT_APPROVED":
      case "DRAFT_REJECTED":
        return `SUBMISSION : ${submissionDate}`;
      case "default":
        return `SUBMISSION : ${submissionDate}`;
      default:
        return "";
    }
  };

  const renderActionButtons = () => {
    switch (status) {
      case "uploading":
        return (
          <FlexColumn
            height="24"
            width="24"
            borderRadius="50%"
            alignItems="center"
            justifyContent="center"
            backgroundColor={colorPalette.popWhite[100]}
            flexShrink={0}
          >
            {/* <Icon
              cursor="pointer"
              onClick={onCancel}
              data-cy={`${dataCy}-cancel`}
              size="13"
              height="24"
              width="24"
              name="fa-sharp fa-solid fa-xmark"
              color={colorPalette.popBlack[500]}
            /> */}
          </FlexColumn>
        );
      case "uploaded":
        return (
          <FlexRow gap="12px" width="unset">
            <FlexColumn
              flexShrink={0}
              height="24"
              width="24"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              backgroundColor={colorPalette.popWhite[100]}
            >
              {/* <Icon
                cursor="pointer"
                onClick={onRetry}
                data-cy={`${dataCy}-retry`}
                size="13"
                height="24"
                width="24"
                name="fa-sharp fa-solid fa-rotate-right"
                color={colorPalette.popBlack[500]}
              /> */}
            </FlexColumn>
            {/* <FlexColumn
              flexShrink={0}
              height="24"
              width="24"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              backgroundColor={colorPalette.popWhite[100]}
            >
              <Icon
                cursor="pointer"
                onClick={onDelete}
                data-cy={`${dataCy}-retry`}
                size="13"
                height="24"
                width="24"
                name="fa-regular fa-trash-can"
                color={colorPalette.popBlack[500]}
              />
            </FlexColumn> */}
          </FlexRow>
        );
      case "failed":
        return (
          <FlexRow gap="12px" width="unset">
            <FlexColumn
              flexShrink={0}
              height="24"
              width="24"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              backgroundColor={colorPalette.error[500]}
            >
              {/* <Icon
                cursor="pointer"
                onClick={onRetry}
                data-cy={`${dataCy}-retry-failed`}
                size="13"
                height="24"
                width="24"
                name="fa-sharp fa-solid fa-rotate-right"
                color={colorPalette.popWhite[500]}
              /> */}
              <ActionButton
                variant="primary"
                onClick={onRetry}
                data-cy={`${dataCy}-retry-failed`}
                leftIcon={
                  <Icon
                    name="fa-sharp fa-solid fa-rotate-right"
                    color="inherit"
                    size="13"
                    height="20"
                    width="20"
                  />
                }
              >
                Retry
              </ActionButton>
            </FlexColumn>

            {/* <FlexColumn
              flexShrink={0}
              height="24"
              width="24"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              backgroundColor={colorPalette.popWhite[100]}
            >
              <Icon
                cursor="pointer"
                onClick={onDelete}
                data-cy={`${dataCy}-delete-failed`}
                size="13"
                height="24"
                width="24"
                name="fa-regular fa-trash-can"
                color={colorPalette.popBlack[500]}
              />
            </FlexColumn> */}
          </FlexRow>
        );
      case "DRAFT_PENDING":
        return (
          <Tags
            type="round"
            label="Under review"
            backgroundColor={colorPalette.warning[500]}
            color={colorPalette.popBlack[500]}
            data-cy={`${dataCy}-status-tag`}
          />
        );
      case "DRAFT_APPROVED":
        return (
          <Tags
            type="round"
            label="Approved"
            backgroundColor={colorPalette.success[500]}
            color={colorPalette.popWhite[500]}
            data-cy={`${dataCy}-status-tag`}
          />
        );
      case "DRAFT_REJECTED":
        return (
          <Tags
            type="round"
            label="Rejected"
            backgroundColor={colorPalette.error[500]}
            color={colorPalette.popWhite[500]}
            data-cy={`${dataCy}-status-tag`}
          />
        );
      case "default":
        return null;
      default:
        return null;
    }
  };

  return (
    <FlexColumn gap="2px" py="4px" data-cy={dataCy}>
      <FlexRow width="unset" gap={8} alignItems="center">
        <Icon
          size="16px"
          height="20px"
          width="20px"
          name="fa-solid fa-file-video"
          color={colorPalette.popBlack[500]}
        />
        <Body6MediumTypography
          textOverflow="ellipsis"
          maxWidth="270px"
          data-cy={`${dataCy}-filename`}
          fontFamily="Lexend"
          color={colorPalette.popBlack[500]}
        >
          {fileName}
        </Body6MediumTypography>
      </FlexRow>
      <FlexRow gap="12px" alignItems="center">
        <FlexRow flex="1">
          {status === "uploading" && (
            <FlexRow gap="8px">
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              <Caps2SemiboldTypography color={colorPalette.popBlack[500]}>
                {progress}%
              </Caps2SemiboldTypography>
            </FlexRow>
          )}
          {status !== "uploading" && (
            <Caps2SemiboldTypography
              data-cy={`${dataCy}-status-text`}
              color={getFileUploadStatusColor(status)}
            >
              {getStatusText(status)}
            </Caps2SemiboldTypography>
          )}
        </FlexRow>

        {renderActionButtons()}
      </FlexRow>
    </FlexColumn>
  );
};

export const FileUploadStatus: React.FC<FileUploadStatusItemProps> = ({
  fileName,
  status,
  progress,
  fileSize,
  submissionDate,
  onCancel,
  onRetry,
  onDelete,
  dataCy = "file-upload-status",
}) => {
  return (
    <Box data-cy={dataCy} width="100%">
      <FileUploadStatusItem
        fileName={fileName}
        status={status}
        progress={progress}
        fileSize={fileSize}
        submissionDate={submissionDate}
        onCancel={onCancel}
        onRetry={onRetry}
        onDelete={onDelete}
        dataCy={dataCy}
      />
    </Box>
  );
};
